import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../../hooks/apiClient';
import { buildUnitsByUuid, mapInventoryArticlesToMaterialOptions } from '../utils/internalOrdersUtils';

const DEBOUNCE_MS = 500;

const searchArticles = async (term) => {
  const results = [];
  const seen = new Set();

  const addArticle = (article) => {
    if (!article?.uuid || seen.has(article.uuid)) return;
    seen.add(article.uuid);
    results.push(article);
  };

  try {
    const searchResponse = await apiClient.get('inventory_articles/search', {
      params: { query: term },
    });
    (searchResponse.data || []).forEach(addArticle);
  } catch (error) {
    if (error.response?.status !== 400) {
      throw error;
    }
  }

  try {
    const codeResponse = await apiClient.get(
      `inventory_articles/by-code/${encodeURIComponent(term)}`
    );
    if (codeResponse.data) {
      addArticle(codeResponse.data);
    }
  } catch (error) {
    if (error.response?.status !== 404) {
      throw error;
    }
  }

  return results;
};

export const useInventoryArticleSearch = (units = [], enabled = true) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  useEffect(() => {
    if (!enabled) {
      setSearchTerm('');
      setDebouncedTerm('');
    }
  }, [enabled]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm.trim());
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const canSearch = enabled && debouncedTerm.length > 0;

  const { data: articles = [], isFetching, isError } = useQuery({
    queryKey: ['inventory_articles', 'search', debouncedTerm],
    queryFn: () => searchArticles(debouncedTerm),
    enabled: canSearch,
    staleTime: 60 * 1000,
    retry: false,
  });

  const unitsByUuid = buildUnitsByUuid(units);
  const materialOptions = mapInventoryArticlesToMaterialOptions(articles, unitsByUuid);

  const resetSearch = () => {
    setSearchTerm('');
    setDebouncedTerm('');
  };

  const trimmedSearch = searchTerm.trim();
  const isPendingDebounce =
    trimmedSearch.length > 0 && trimmedSearch !== debouncedTerm;

  return {
    searchTerm,
    setSearchTerm,
    materialOptions,
    isSearching: isFetching && canSearch,
    isPendingDebounce,
    hasSearchError: isError,
    resetSearch,
  };
};
